import challengeFields from "../inputsConfig";

const { timed: timedFields } = challengeFields;
export default {
  accordion: true,
  items: {
    "Rewards & Challenge Details": [
      {
        name: "Reward Policy",
        required: true,
        description:
          "Select how rewards will be distributed to participants based on their performance",
        fields: timedFields.reward_policy,
      },
      {
        name: "Total rewards",
        required: true,
        description: "Enter the total reward amount",
        fields: timedFields.reward_points,
      },
      {
        name: "Deadline",
        required: true,
        fields: timedFields.deadline,
      },
      {
        name: "Challenge image",
        required: true,
        description:
          "Upload the image you would like to represent this challenge. If you'd prefer to use a video, skip this question and process to the next one.",
        fields: timedFields.snapshot_pic,
      },
      {
        name: "Link to video",
        required: false,
        description:
          "Enter youtube video link here to replace the challenge image with a video. Optional",
        fields: timedFields.link_to_video,
      },
      {
        name: "Challenge title",
        required: true,
        description: "Provide a title for this challenge.",
        fields: timedFields.title,
      },
      {
        name: "Challenge description",
        required: true,
        description:
          "Provide a brief description of the challenge and its purpose. Drag and drop images. Copy + Paste image/video URLs and they will be automatically embedded.",
        fields: timedFields.description,
      },
      {
        name: "Prize description",
        required: true,
        description: "Provide a brief description of the prizes to be awarded.",
        fields: timedFields.short_description,
      },
      {
        name: "Deliverables",
        required: true,
        fields: timedFields.deliverable_submission_fields,
      },
      {
        name: "Evaluation Scheme",
        description:
          "Personalize how challengers evaluate submissions on their dashboard. This cannot be changed once the challenge is launched!",
        required: false,
        fields: [
          timedFields["contest_evaluation_scheme.evaluation_rubric"],
          timedFields["contest_evaluation_scheme.pairwise_rating_scheme"],
        ],
      },
      {
        name: "Feedback tags",
        description:
          "Tags that will be shown to the solver when they submit a solution. These tags will be used to rate the solution.",
        required: false,
        fields: timedFields.feedback_tags,
      },
    ],
    "Supplementals, Add-ons, Targeting": [
      {
        name: "Solution Time Limit",
        description:
          "Creates a time limit for the solution in minutes. Time limits restrict access to editing a solution once the countdown has expired. Set the value to 0 for no time limit. Note: Enabling this feature will limit solvers to only 1 solution that is not editable.",
        required: true,
        fields: timedFields.draft_to_submission_time_limit_in_seconds,
      },
      {
        name: "Supplemental Submission Fields",
        required: false,
        fields: timedFields.supplemental_submission_fields,
      },
      {
        name: "Include AI Submission",
        description:
          "Select yes for an AI-generated submission to be added to the challenge dashboard",
        required: false,
        fields: timedFields.generate_ai_solution,
      },
      {
        name: "Contest Visibility",
        description:
          "Choose if the challenge must be visible to everybody or just logged in users.",
        required: false,
        fields: timedFields.privacy_type,
      },
      {
        name: "Solution Sources",
        description:
          'Sources the solver consulted when creating the solution (ex. articles, videos, etc.). Set value to "0" to remove solution sources',
        required: true,
        fields: timedFields.max_bibliographies_count,
      },
      {
        name: "Target Problem Solvers",
        description:
          "Problem solvers in the selected tier will be able to access the challenge.",
        required: false,
        fields: timedFields["target_problem_solvers.criteria"],
      },
      {
        name: "Target Problem Solver Criteria",
        description:
          "Problem solvers that meet any of the above criteria will be able to access the challenge.",
        required: false,
        fields: timedFields.contest_permissions,
      },
      {
        name: "Strict Problem Solver Criteria",
        description:
          "By selecting 'Yes', top-tier solvers will not bypass location restriction testing for access to challenge.",
        required: false,
        fields: timedFields.strict_contest_permissions,
      },
    ],
    "Restrictions & Control": [
      {
        name: "Auto-approve Solutions",
        description:
          "By selecting 'Yes', all solutions will be automatically approved and visible to clients without the need for MindSumo admin approval",
        required: true,
        fields: timedFields.auto_approve_submissions,
      },
      {
        name: "First-time Solution Restrictions",
        description:
          "By selecting “yes” first-time and unrated solvers will not be able to upload attachments or paste content when creating a submission. Submissions also need to be at least 200 characters long.",
        required: true,
        fields: timedFields.restricted_for_1st_time_solver,
      },
      {
        name: "Unapproved solution cap for challenge",
        description:
          "By selecting “yes” first-time and unrated solvers will have to reserve a spot in order to submit their solution",
        required: true,
        fields: [
          timedFields.cap_enabled,
          timedFields.cap_limit,
          timedFields.cap_submit_time_limit_in_seconds,
        ],
      },
      {
        name: "Approved solution cap for challenge",
        description:
          "By selecting “yes” first-time and unrated solvers will not be able to submit if the approved solution number hits the chosen number.",
        required: true,
        fields: [
          timedFields.approved_cap_enabled,
          timedFields.approved_cap_limit,
        ],
      },
      {
        name: "Show Criteria in Deliverables",
        description:
          "Typically will allow problem solvers to create better submissions. When hidden, also hides submission count on the challenge page.",
        required: false,
        fields: timedFields.show_criteria,
      },
      {
        name: "Comment Controls for Solvers",
        required: false,
        fields: [
          timedFields.comments_visible_to_challengers,
          timedFields.comments_require_approvals,
        ],
      },
      {
        name: "Uses MindSumo Community",
        description:
          "When a challenge doesn't use MindSumo community (e.g. employee challenges), we change some parts of the experience such as not displaying ratings to participants.",
        required: true,
        fields: timedFields.no_community_contest,
      },
      {
        name: "Anonymous Challenge",
        description:
          "When challenges are anonymous, masks identifying information about the company. Problem solvers view visible companies more highly, so standard is preferred.",
        required: true,
        fields: timedFields.anonymous,
      },
      {
        name: "Require Registered Problem Solvers",
        description:
          "When required, a user must sign in/register as a problem solver in order to solve.",
        required: false,
        fields: timedFields.require_user_login,
      },
      {
        name: "Submissions per Problem Solver",
        description:
          "Allows problem solvers to submit multiple times. They can only work on one submission at a time, but can edit existing solutions. Users can win multiple prizes, as we reward by submission, not user.",
        required: true,
        fields: timedFields.multiple_solution_count,
      },
      {
        name: "Goal of this Challenge",
        description:
          "There will be minor UI changes. For innovation challenges, we will prioritize rating tags over other things, and for insights challenges, we will prioritize custom tags and sentiment over rating tags",
        required: true,
        fields: timedFields.contest_goal,
      },
      {
        name: "Show in Upcoming Section?",
        description:
          "Do you want to display this challenge to problem solvers in the Upcoming challenges section. It will be displayed in upcoming prior to launching. Problem solvers will be able to add this challenge to their watchlist to express their interest.",
        required: true,
        fields: [
          timedFields.show_in_upcoming,
          timedFields.expected_launch_date,
        ],
      },
      {
        name: "Solution Privacy",
        description:
          "When solutions are marked as public, they might be publized to MindSumo users and used for marketing purposes. It is great for everybody if solutions are public.",
        required: true,
        fields: timedFields.solutions_private,
      },
      {
        name: "Custom URL",
        required: false,
        fields: timedFields.url_title,
      },
      {
        name: "Hide Entities from Word cloud",
        description:
          "Admin Only. Enter words separated by commas that you don't want to appear in the word cloud",
        required: false,
        fields: timedFields.hide_entities,
      },
    ],
    "Challenge Client & Owner": [
      {
        name: "Rep and Email",
        required: true,
        fields: [timedFields.rep_email, timedFields.manager_email],
      },
      {
        name: "Select Company Team",
        required: true,
        fields: timedFields.company_team_id,
      },
      {
        name: "Company Contact",
        required: true,
        fields: [timedFields.contact_name, timedFields.contact_email],
      },
    ],
    "Style Customization": [
      {
        name: "Custom banner color",
        description:
          "This color will replace the standard MindSumo header bar.",
        required: false,
        fields: [
          timedFields.custom_banner_color_helper,
          timedFields["contest_layout_customization.custom_banner_color"],
        ],
      },
      {
        name: "Custom banner logo",
        required: false,
        fields: [
          timedFields["contest_layout_customization.custom_banner_logo"],
          timedFields["contest_layout_customization.remove_custom_banner_logo"],
        ],
      },
      {
        name: "Custom banner title",
        description:
          "Enter your custom title to appear next to the logo in the banner.",
        required: false,
        fields: timedFields["contest_layout_customization.custom_banner_text"],
      },
      {
        name: "Hide header links?",
        description:
          "Choose if the page should contain the standard MindSumo header links.",
        required: false,
        fields: timedFields["contest_layout_customization.hide_header_layout"],
      },
      {
        name: "Hide footer links?",
        description:
          "Choose if the page should contain the standard MindSumo footer links (hide the upper part of the footer?).",
        required: false,
        fields: timedFields["contest_layout_customization.hide_footer_layout"],
      },
      {
        name: "Hide demographics section on dashboard?",
        description:
          "Choose if the demographics should be hidden on the challenge dashboard.",
        required: false,
        fields:
          timedFields[
            "contest_layout_customization.hide_demographics_section_on_dashboard"
          ],
      },
    ],
    "Extra Materials": [
      {
        name: "Extra Materials",
        required: false,
        fields: timedFields.contestfiles,
      },
    ],
  },
};
